import React, { useMemo } from "react";
import { CheckCircle2, XCircle, ClockIcon } from "lucide-react";
import { DateTimeUtil } from "../../utils";
import { NgwAvatar } from "../common";

interface ApprovalHistoryProps {
    workflowDetails?: any[];
    showTitle?: boolean;
    title?: string;
    className?: string;
}

interface ActedItem {
    actor: any;
    action: string;
    date?: string;
    note?: string;
    status: "APPROVED" | "REJECTED";
    employeeId?: string;
}

const ApprovalHistory: React.FC<ApprovalHistoryProps> = ({
    workflowDetails = [],
    showTitle = true,
    title = "Approval history",
    className = "",
}) => {
    const processedSteps = useMemo(() => {
        if (!workflowDetails || workflowDetails.length === 0) return [];

        return workflowDetails.map((step) => {
            const actors = Object.values(step.actor_json || {});
            const actorNames = actors.map((actor: any) => actor.name).join(", ");

            // Collect all acted results (approved/rejected) for this step
            const acted: ActedItem[] = [];
            const seenEmployeeIds = new Set<string>();

            const isStepApproved =
                step.wf_task_acted_json?.APPROVED === 1 ||
                step.wf_task_acted_json?.APPROVED === "1";
            const isStepRejected =
                step.wf_task_acted_json?.APPROVED === 0 ||
                step.wf_task_acted_json?.APPROVED === "0";

            // Helper function to get employee_id from actor object
            const getEmployeeId = (actor: any): string | undefined => {
                return actor?.employee_id || actor?.id || actor?.emp_account_id;
            };

            // Helper function to add acted item if not duplicate
            const addActedItem = (actor: any, status: "APPROVED" | "REJECTED", date?: string, note?: string) => {
                const employeeId = getEmployeeId(actor);
                if (employeeId && seenEmployeeIds.has(employeeId)) {
                    return; // Skip duplicate
                }
                if (employeeId) {
                    seenEmployeeIds.add(employeeId);
                }
                acted.push({
                    actor,
                    action: status === "APPROVED" ? "Approved" : "Rejected",
                    date,
                    note,
                    status,
                    employeeId,
                });
            };

            // First, check APPROVED_BY (single approver)
            if (isStepApproved && step.wf_task_acted_json?.APPROVED_BY) {
                addActedItem(
                    step.wf_task_acted_json.APPROVED_BY,
                    "APPROVED",
                    step.wf_task_acted_json?.APPROVED_ON,
                    step.wf_task_acted_json?.APPROVAL_NOTE
                );
            } else if (isStepRejected && step.wf_task_acted_json?.APPROVED_BY) {
                addActedItem(
                    step.wf_task_acted_json.APPROVED_BY,
                    "REJECTED",
                    step.wf_task_acted_json?.APPROVED_ON,
                    step.wf_task_acted_json?.APPROVAL_NOTE
                );
            }

            // Then, check RESULT arrays (multiple approvers) - only add if not already seen
            // if (step.wf_task_acted_json?.RESULT) {
            //     const approvedList = step.wf_task_acted_json.RESULT.APPROVED || [];
            //     const rejectedList = step.wf_task_acted_json.RESULT.REJECTED || [];

            //     approvedList.forEach((actor: any) => {
            //         addActedItem(
            //             actor,
            //             "APPROVED",
            //             step.wf_task_acted_json?.APPROVED_ON,
            //             step.wf_task_acted_json?.APPROVAL_NOTE
            //         );
            //     });

            //     rejectedList.forEach((actor: any) => {
            //         addActedItem(
            //             actor,
            //             "REJECTED",
            //             step.wf_task_acted_json?.APPROVED_ON,
            //             step.wf_task_acted_json?.APPROVAL_NOTE
            //         );
            //     });
            // }

            const isPending = !acted.length && step.current_status === "0";
            const isRejected =
                step.current_status === "0" &&
                (step.status_text || "").toLowerCase().includes("reject");
            const isApproved = step.current_status === "1" || acted.some((a) => a.status === "APPROVED");

            return {
                step,
                actors,
                actorNames,
                acted,
                isPending,
                isRejected,
                isApproved,
            };
        });
    }, [workflowDetails]);

    if (!workflowDetails || workflowDetails.length === 0) {
        return null;
    }

    return (
        <div className={`mt-6 ${className}`}>
            {showTitle && (
                <p className="font-semibold text-gray-800 mb-2 flex items-center">
                    <svg
                        className="w-4 h-4 mr-2 text-gray-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                    </svg>
                    {title}
                </p>
            )}
            <div className="bg-gray-50 rounded-lg p-4 border">
                <div className="flex flex-col space-y-0 relative">
                    {processedSteps.map((processed, idx) => {
                        const { step, actors, actorNames, acted, isPending, isRejected, isApproved } = processed;
                        return (
                            <div key={idx} className="flex items-stretch relative min-h-[80px]">
                                {/* Draw vertical line to connect steps visually, except for last step */}
                                {idx !== processedSteps.length - 1 && (
                                    <div className="absolute left-4 top-8 w-px h-[calc(100%_-_16px)] bg-gray-200 z-0"></div>
                                )}
                                {/* Avatars: show up to 2 side by side, smaller size */}
                                <div className="flex flex-col items-center mr-4 z-10">
                                    <div className="flex -space-x-2">
                                        {actors.slice(0, 2).map((actor: any, i: number) =>
                                            actor.image_url ? (
                                                <img
                                                    key={i}
                                                    src={actor.image_url}
                                                    alt={actor.name}
                                                    className="w-8 h-8 rounded-full object-cover border border-gray-200 shadow-sm bg-gray-100"
                                                />
                                            ) : (
                                                <div
                                                    key={i}
                                                    className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-400 border border-gray-200 shadow-sm"
                                                >
                                                    <svg
                                                        className="w-5 h-5"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        strokeWidth="2"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            d="M5.121 17.804A9 9 0 1112 21a8.963 8.963 0 01-6.879-3.196z"
                                                        />
                                                    </svg>
                                                </div>
                                            )
                                        )}
                                    </div>
                                </div>
                                {/* Step details: actor names, actions, status, notes */}
                                <div className="flex-1 flex flex-col justify-center">
                                    <span className="font-semibold text-gray-900 text-base leading-tight">
                                        {actorNames || "-"}
                                    </span>
                                    {/* Pending state */}
                                    {isPending && (
                                        <div className="mt-1 mb-3 flex items-center space-x-2">
                                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold text-gray-500 bg-gray-100 mr-2">
                                                <ClockIcon className="w-4 h-4 mr-1 text-gray-400" />
                                                Pending
                                            </span>
                                        </div>
                                    )}
                                    {/* Acted (approved/rejected) */}
                                    {acted.length > 0 &&
                                        acted.map((item, i) => (
                                            <div key={i} className="mt-1 mb-3">
                                                <div className="flex items-center">
                                                    {/* Status badge with actor name and date in sentence format */}
                                                    {item.status === "APPROVED" && (
                                                        <span className="inline-flex items-center text-sm">
                                                            <CheckCircle2 className="w-4 h-4 mr-1 text-green-600" />
                                                            <span className="text-gray-800">
                                                                Approved by{" "}
                                                                <span className="font-medium">
                                                                    {item.actor?.name ?? item.actor ?? "-"}
                                                                </span>
                                                                {item.date && (
                                                                    <span className="text-gray-600">
                                                                        {" "}
                                                                        on{" "}
                                                                        {DateTimeUtil.formatDateRangeFancy(
                                                                            item.date,
                                                                            item.date,
                                                                            true
                                                                        )}
                                                                    </span>
                                                                )}
                                                            </span>
                                                        </span>
                                                    )}
                                                    {item.status === "REJECTED" && (
                                                        <span className="inline-flex items-center text-sm">
                                                            <XCircle className="w-4 h-4 mr-1 text-red-600" />
                                                            <span className="text-gray-800">
                                                                Rejected by{" "}
                                                                <span className="font-medium">
                                                                    {item.actor?.name ?? item.actor ?? "-"}
                                                                </span>
                                                                {item.date && (
                                                                    <span className="text-gray-600">
                                                                        {" "}
                                                                        on{" "}
                                                                        {DateTimeUtil.formatDateRangeFancy(
                                                                            item.date,
                                                                            item.date,
                                                                            true
                                                                        )}
                                                                    </span>
                                                                )}
                                                            </span>
                                                        </span>
                                                    )}
                                                </div>
                                                {/* Show approval/rejection note if present */}
                                                {item.note && (
                                                    <span className="block mt-1 text-gray-500 text-sm italic pl-5">
                                                        Note: {item.note}
                                                    </span>
                                                )}
                                            </div>
                                        ))}
                                    {/* Fallback: show current status and any notes if no one has acted yet and not pending */}
                                    {!isPending && acted.length === 0 && (
                                        <div className="mt-1 mb-3">
                                            {step.status_text && (
                                                <span className="text-gray-500 text-xs block">
                                                    {step.status_text}
                                                </span>
                                            )}
                                            {isRejected && (
                                                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold text-red-600 bg-red-50 mr-2">
                                                    <XCircle className="w-4 h-4 mr-1" />
                                                    Rejected
                                                </span>
                                            )}
                                            {isApproved && (
                                                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold text-green-600 bg-green-50 mr-2">
                                                    <CheckCircle2 className="w-4 h-4 mr-1" />
                                                    Approved
                                                </span>
                                            )}
                                            {step.current_status === "0" && !isRejected && !isApproved && (
                                                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold text-gray-500 bg-gray-100 mr-2">
                                                    <ClockIcon className="w-4 h-4 mr-1 text-gray-400" />
                                                    Pending
                                                </span>
                                            )}
                                            {step.wf_task_acted_json?.APPROVAL_NOTE && (
                                                <span className="block mt-1 text-gray-500 text-xs italic">
                                                    {step.wf_task_acted_json.APPROVAL_NOTE}
                                                </span>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default ApprovalHistory;

